import {
    Component,
    Input,
    Output,
    EventEmitter,
    OnChanges,
    SimpleChanges,
    AfterViewChecked,
    HostListener,
} from "@angular/core";
import { ComponentBase } from "../../models/ComponentBase";
import {
    Customer,
    Department,
    ResourceItem,
    UpsertUserRequest,
    UserRole,
    UserRight,
    UserRoleAdmin,
    UserRoleStorage,
    UserRoleSustainability,
    UserRoleTerminal,
    ResourceClient,
    GetResourceRequest,
} from "../../WebApiClient";
import { CustomerService } from "../../services/customer.service";
import { DepartmentService } from "../../services/department.service";
import {
    UserRoles,
    UserRolesAdmin,
    UserRolesStorage,
    UserRolesSustainability,
    UserRolesTerminal,
} from "../../Constants";
import { Guid } from "../../models/Guid";
import { LoginService } from "../../services/login.service";
import {
    UntypedFormControl,
    UntypedFormGroup,
    Validators,
} from "@angular/forms";
@Component({
    selector: "app-edit-user-inner",
    templateUrl: "./edit-user-inner.component.html",
    styleUrls: [
        "./edit-user-inner.component.scss",
        "../../style/form-table.scss",
    ],
    standalone: false,
    providers: [ResourceClient]
})
export class EditUserInnerComponent
    extends ComponentBase
    implements OnChanges
{
    public rnd: string;

    editUserFormGroup: UntypedFormGroup = new UntypedFormGroup({});
    customerIsRequiredMark: boolean = false;
    departmentDropdownOpen = false;

    constructor(customerService: CustomerService, private readonly loginService: LoginService, private readonly departmentService: DepartmentService, private readonly resourceClient: ResourceClient,) {
        super();
        this.rnd = Guid.newGuid();
        let cusSub = customerService.customersObservable.subscribe((cs) => {
            this.customers = cs;
        });
        this.subscriptions.push(cusSub);
        const request = new GetResourceRequest({
            type: "0"
        });
        this.subscriptions.push(
            this.resourceClient
                .getResourceList(request)
                .subscribe(response => {
                    this.resources = response.resources;
                })
        );

        this.departmentService.getAllDepartments().subscribe(res => {
            this.departments = res.departments;
        });

        if (loginService.hasRights(UserRight.EditUserAdmin)) {
            this.roles = UserRoles;
        } else {
            this.roles = UserRoles.filter(
                (ur) => ur.key !== UserRole.ProjectManager
            );
        }

        this.roles = UserRoles;
        this.rolesAdmin = UserRolesAdmin;
        this.rolesStorage = UserRolesStorage;
        this.rolesSustainability = UserRolesSustainability;
        this.rolesTerminal = UserRolesTerminal;
    }

    ngOnChanges(changes: SimpleChanges): void {
        this.resetForm();
    }

    toggleDepartmentDropdown() {
        this.departmentDropdownOpen = !this.departmentDropdownOpen;
        this.departments = this.departments?.sort((a, b) =>
            a.name!.localeCompare(b.name!, 'da-DK')
        );
    }

    isDepartmentSelected(id: string): boolean {
        return (this.departmentControl.value ?? []).includes(id);
    }

    toggleDepartment(id: string, checked: boolean) {
        const current = this.departmentControl.value ?? [];

        if (checked) {
            this.departmentControl.setValue([...current, id]);
        } else {
            this.departmentControl.setValue(current.filter((d: string) => d !== id));
        }
    }

    getDepartmentsDisplay(): string {
        const selected = this.departmentControl.value ?? [];

        if (!selected.length) return '';

        const names = this.departments
            ?.filter(d => selected.includes(d.id))
            .map(d => d.name) ?? [];

        if (!names.length) return '';

        if (names.length === 1) return names[0]!;

        return `${names[0]} (+${names.length - 1})`;
    }

    @HostListener('document:click', ['$event'])
    onClickOutside(event: MouseEvent) {
        const target = event.target as HTMLElement;

        if (!target.closest('.dropdown')) {
            this.departmentDropdownOpen = false;
        }
    }

    resetForm(): void {
        this.editUserFormGroup = new UntypedFormGroup({
            name: new UntypedFormControl("", Validators.required),
            customer: new UntypedFormControl(""),
            resource: new UntypedFormControl(""),
            departments: new UntypedFormControl([]),
            email: new UntypedFormControl("", [
                Validators.required,
                Validators.pattern(/^[\W\w]+@([\w-]+\.)+[\w-]{2,4}$/),
            ]),
            username: new UntypedFormControl("", Validators.required),
            password: new UntypedFormControl(""),
            userRole: new UntypedFormControl("", Validators.required),
            userRoleAdmin: new UntypedFormControl("", Validators.required),
            userRoleStorage: new UntypedFormControl("", Validators.required),
            userRoleSustainability: new UntypedFormControl(
                "",
                Validators.required
            ),
            userRoleTerminal: new UntypedFormControl("", Validators.required),
        });

        let editUserFormGroupSub = this.editUserFormGroup.valueChanges.subscribe(() => {
            if (this.editUserFormGroup.dirty && this.editUserFormGroup.touched) {
                this.formIsDirty.emit(true);
            }
        });

        this.subscriptions.push(editUserFormGroupSub);

        this.bindData();
    }

    bindData(): void {
        if (this.user) {
            if (!this.newUser) {
                this.nameControl.setValue(this.user.name);
                this.customerControl.setValue(this.user.customerId);
                this.resourceControl.setValue(this.user.resourceId);
                this.departmentControl.setValue(this.user.departmentIds ?? []);
                this.emailControl.setValue(this.user.email);
                this.usernameControl.setValue(this.user.username);
                this.userRoleControl.setValue(this.user.userRole);
                this.userRoleAdminControl.setValue(
                    this.user.userRoleAdmin ?? ""
                );
                this.userRoleStorageControl.setValue(
                    this.user.userRoleStorage ?? ""
                );
                this.userRoleSustainabilityControl.setValue(
                    this.user.userRoleSustainability ?? ""
                );
                this.userRoleTerminalControl.setValue(
                    this.user.userRoleTerminal ?? ""
                );

                this.editUserFormGroup.markAllAsTouched();
            }

            if (this.newUser) {
                this.passwordControl.setValidators(Validators.required);
                this.passwordControl.updateValueAndValidity();
            }

            let editUserFormGroupValueChangeSub =
                this.editUserFormGroup.valueChanges.subscribe((cs) => {
                    if (!this.editUserFormGroup.valid) {
                        return;
                    }

                    this.user.name = this.nameControl.value;
                    this.user.customerId = this.customerControl.value;
                    this.user.resourceId = this.resourceControl.value;
                    this.user.departmentIds = this.departmentControl.value;
                    this.user.email = this.emailControl.value;
                    this.user.username = this.usernameControl.value;
                    this.user.userRole = this.userRoleControl.value;
                    this.user.userRoleAdmin = this.userRoleAdminControl.value;
                    this.user.userRoleStorage =
                        this.userRoleStorageControl.value;
                    this.user.userRoleSustainability =
                        this.userRoleSustainabilityControl.value;
                    this.user.userRoleTerminal =
                        this.userRoleTerminalControl.value;
                    this.user.password = this.passwordControl.value;
                });

            this.subscriptions.push(editUserFormGroupValueChangeSub);

            let editUserFormGroupStatusChangeSub =
                this.editUserFormGroup.statusChanges.subscribe((cs) => {
                    this.formIsValid.emit(this.editUserFormGroup.valid);
                });

            this.subscriptions.push(editUserFormGroupStatusChangeSub);

            let userRoleControlChangeSub =
                this.userRoleControl.valueChanges.subscribe((cs) => {
                    this.customerIsRequired();
                });

            this.subscriptions.push(userRoleControlChangeSub);

            let userRoleStorageControlChangeSub =
                this.userRoleStorageControl.valueChanges.subscribe((cs) => {
                    this.customerIsRequired();
                });

            this.subscriptions.push(userRoleStorageControlChangeSub);

            let userRoleSustainabilityControlChangeSub =
                this.userRoleSustainabilityControl.valueChanges.subscribe(
                    (cs) => {
                        this.customerIsRequired();
                    }
                );

            this.subscriptions.push(userRoleSustainabilityControlChangeSub);

            let userRoleTerminalControlChangeSub =
                this.userRoleTerminalControl.valueChanges.subscribe((cs) => {
                    this.customerIsRequired();
                });

            this.subscriptions.push(userRoleTerminalControlChangeSub);

            setTimeout(() => {
                this.editUserFormGroup.updateValueAndValidity();
            }, 10);
        }
    }

    customers: Customer[] | undefined;
    departments: Department[] | undefined;
    resources: ResourceItem[] | undefined

    readonly roles: { key: UserRole; displayName: string }[];
    readonly rolesAdmin: { key: UserRoleAdmin; displayName: string }[];
    readonly rolesStorage: { key: UserRoleStorage; displayName: string }[];
    readonly rolesSustainability: {
        key: UserRoleSustainability;
        displayName: string;
    }[];
    readonly rolesTerminal: { key: UserRoleTerminal; displayName: string }[];

    @Input()
    user: UpsertUserRequest = new UpsertUserRequest();

    @Input()
    newUser: boolean | undefined = false;

    @Output()
    formIsValid = new EventEmitter<boolean>();

    @Output()
    formIsDirty = new EventEmitter<boolean>();

    get userRole(): UserRole {
        return this.user.userRole;
    }
    set userRole(val: UserRole) {
        this.user.userRole = val;
        if (val != UserRole.Customer) {
            this.user.customerId = undefined;
        }
    }
    get nameControl(): UntypedFormControl {
        return this.getFormControl("name");
    }
    get customerControl(): UntypedFormControl {
        return this.getFormControl("customer");
    }
    get departmentControl(): UntypedFormControl {
        return this.getFormControl("departments");
    }
    get resourceControl(): UntypedFormControl {
        return this.getFormControl("resource");
    }
    get emailControl(): UntypedFormControl {
        return this.getFormControl("email");
    }
    get usernameControl(): UntypedFormControl {
        return this.getFormControl("username");
    }
    get userRoleControl(): UntypedFormControl {
        return this.getFormControl("userRole");
    }
    get userRoleAdminControl(): UntypedFormControl {
        return this.getFormControl("userRoleAdmin");
    }
    get userRoleStorageControl(): UntypedFormControl {
        return this.getFormControl("userRoleStorage");
    }
    get userRoleSustainabilityControl(): UntypedFormControl {
        return this.getFormControl("userRoleSustainability");
    }
    get userRoleTerminalControl(): UntypedFormControl {
        return this.getFormControl("userRoleTerminal");
    }
    get passwordControl(): UntypedFormControl {
        return this.getFormControl("password");
    }

    private getFormControl(name: string): UntypedFormControl {
        return this.editUserFormGroup.get(name) as UntypedFormControl;
    }

    private customerIsRequired(): void {
        if (this.userRoleControl.value === UserRole.Customer) {
            this.customerControl.setValidators(Validators.required);
            this.customerControl.updateValueAndValidity();
            this.customerIsRequiredMark = true;
            return;
        }

        if (
            this.userRoleStorageControl.value ===
                UserRoleStorage.ReadCustomer ||
            this.userRoleStorageControl.value === UserRoleStorage.EditCustomer
        ) {
            this.customerControl.setValidators(Validators.required);
            this.customerControl.updateValueAndValidity();
            this.customerIsRequiredMark = true;
            return;
        }

        if (
            this.userRoleSustainabilityControl.value ===
            UserRoleSustainability.ReadCustomer
        ) {
            this.customerControl.setValidators(Validators.required);
            this.customerControl.updateValueAndValidity();
            this.customerIsRequiredMark = true;
            return;
        }


        this.customerControl.setValidators([]);
        this.customerControl.updateValueAndValidity();
        this.customerIsRequiredMark = false;
    }
}
